<template>
  <div class="col-12 col-xxl-12 ">
    <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
            {{ $t('employeesTable.title') }}
          </h6>
        </div>
      </div>

      <div class="card-body px-0 pb-0 pb-2 pe-xl-3">
        <div class="table-responsive p-1 p-xl-4">
          <LoadingSpinner v-if="loading && employees.length === 0" :message="$t('employeesTable.loading')" class="py-5" />

          <template v-else>
            <div class="d-block d-xxl-none">
              <div v-if="!loading && employees.length === 0" class="text-center py-3 text-muted">
                <i class="fas fa-users fa-2x mb-2"></i>
                <p>{{ $t('employeesTable.noEmployees') }}</p>
              </div>

              <div v-for="employee in employees" :key="'mobile-' + employee.id" class="card mb-3 mx-2">
                <div class="card-body py-2">
                  <div class="d-flex align-items-center">
                    <div class="avatar-container me-2">
                      <img :src="employee.image_url || '/images/default-avatar.png'" class="user-avatar rounded-circle"
                           style="width: 40px; height: 40px" @error="handleImageError" />
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="mb-0 text-sm">{{ employee.username }}</h6>
                      <p class="text-xs text-muted mb-0">{{ employee.job_title?.name || '—' }}</p>
                    </div>
                    <span class="badge badge-sm" :class="employee.is_active ? 'bg-success' : 'bg-danger'">
                      {{ employee.is_active ? $t('employeesTable.active') : $t('employeesTable.inactive') }}
                    </span>
                  </div>

                  <div class="mt-2">
                    <p class="text-xs text-muted mb-1">{{ employee.salary }} {{ $t('common.currency') }}</p>
                  </div>

                  <div class="d-flex justify-content-end mt-2">
                    <router-link :to="{ name: 'employees.edit', params: { id: employee.id } }"
                      class="btn btn-link text-secondary p-0 mx-1" v-can="'employees.update'">
                      <i class="fas fa-edit fa-xs"></i>
                    </router-link>
                    <button @click="openDeleteDialog(employee)" class="btn btn-link text-danger p-0 mx-1" v-can="'employees.delete'">
                      <i class="fas fa-trash fa-xs"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <table class="table align-items-center mb-0 d-none d-xxl-table">
              <thead>
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('employeesTable.image') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('employeesTable.name') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('employeesTable.jobTitle') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('employeesTable.gender') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('employeesTable.salary') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('employeesTable.status') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4">{{ $t('employeesTable.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!loading && employees.length === 0">
                  <td colspan="7" class="text-center py-4 text-muted">
                    <i class="fas fa-users fa-2x mb-2"></i>
                    <p>{{ $t('employeesTable.noEmployees') }}</p>
                  </td>
                </tr>

                <template v-if="!loading">
                  <tr v-for="employee in employees" :key="employee.id">
                    <td>
                      <div class="avatar-container">
                        <img :src="employee.image_url || '/images/default-avatar.png'" class="user-avatar rounded-circle" @error="handleImageError" />
                      </div>
                    </td>
                    <td>
                      <div class="d-flex px-3 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-md">{{ employee.username }}</h6>
                          <p class="text-xs text-muted mb-0">{{ employee.email }}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0">{{ employee.job_title?.name || '—' }}</p>
                    </td>
                    <td class="align-middle text-center">
                      <span class="badge badge-sm" :class="employee.gender === 'M' ? 'bg-primary' : 'bg-secondary'">
                        {{ employee.gender === 'M' ? $t('employeesTable.male') : $t('employeesTable.female') }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-xs font-weight-bold">{{ employee.salary }} {{ $t('common.currency') }}</span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="badge badge-sm" :class="employee.is_active ? 'bg-success' : 'bg-danger'">
                        {{ employee.is_active ? $t('employeesTable.active') : $t('employeesTable.inactive') }}
                      </span>
                    </td>
                    <td class="align-middle text-end pe-4">
                      <div class="d-inline-flex">
                        <router-link :to="{ name: 'employees.edit', params: { id: employee.id } }"
                          class="btn btn-link text-secondary p-1 mx-1" v-can="'employees.update'">
                          <i class="fas fa-edit fa-sm"></i>
                        </router-link>
                        <button @click="openDeleteDialog(employee)" class="btn btn-link text-danger p-1 mx-1" v-can="'employees.delete'">
                          <i class="fas fa-trash fa-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits } from "vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";

defineProps({
  employees: { type: Array, required: true, default: () => [] },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(["delete-employee"]);
const openDeleteDialog = (employee) => emit("delete-employee", employee);
const handleImageError = (event) => { event.target.src = '/images/default-avatar.png'; };
</script>
